/**
 * User-related utility functions
 */

/**
 * Generate initials from a name or email
 * Returns first + last initial if available, otherwise first initial.
 * Parenthetical suffixes are stripped before extraction.
 *
 * @param name - Display name
 * @param email - Email (fallback if name is empty)
 * @returns Initials (1-2 uppercase characters)
 *
 * @example
 * getInitials("John Doe") // Returns "JD"
 * getInitials("Jane") // Returns "J"
 * getInitials("Mark (James' father)") // Returns "M"
 * getInitials("", "jane@example.com") // Returns "J"
 * getInitials("") // Returns "U"
 */
export function getInitials(name: string, email?: string): string {
  if (name && name.trim()) {
    // Strip parenthetical content (e.g. "Mark (James' father)" → "Mark")
    const strippedName = name.replace(/\s*\([^)]*\)/g, '').trim();
    // Filter out empty strings from split (handles multiple spaces)
    const names = strippedName.split(' ').filter((n) => n.length > 0);
    const first = names[0];
    const last = names[names.length - 1];
    if (names.length >= 2 && first && last) {
      return (first.charAt(0) + last.charAt(0)).toUpperCase();
    }
    if (names.length === 1 && first && first.length > 0) {
      return first.charAt(0).toUpperCase();
    }
  }

  if (email && email.trim().length > 0) {
    return email.trim().charAt(0).toUpperCase();
  }

  return 'U';
}
